// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import TextArea, { TextAreaProps, TextAreaPresentationProps } from "@store/MobiusOverrides/TextArea/TextArea";
import validateRequiredTextField from "@store/Framework/Common/Utilities/validateRequiredTextField";
import { BaseFormFieldProps, FormFieldValue } from "@store/Framework/Types/FormField";
import React, { FC, useEffect, useState } from "react";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";

type FieldValueType = string;
export type TextAreaFormFieldValue = FormFieldValue<FieldValueType>;

export interface TextAreaFormFieldPresentationProps extends ExpandStyles<TextAreaFormFieldStyles> {}

export interface TextAreaFormFieldProps
    extends Omit<TextAreaProps, keyof TextAreaPresentationProps | "defaultValue" | "error" | "value">,
        TextAreaFormFieldPresentationProps,
        BaseFormFieldProps<FieldValueType> {
    requiredErrorMessage?: string;
    minLengthErrorMessage?: string;
}

export interface TextAreaFormFieldStyles {
    textArea?: TextAreaPresentationProps;
}

export const textAreaFormFieldStyles: TextAreaFormFieldStyles = {};

const TextAreaFormField: FC<TextAreaFormFieldProps> = ({
    defaultValue = "",
    extendedStyles,
    label,
    minLength,
    minLengthErrorMessage,
    onBlur,
    onChange,
    onUpdate,
    onValidate,
    overridedStyles,
    required,
    requiredErrorMessage,
    showError = true,
    ...textAreaProps
}) => {
    const styles = useExpandProps(textAreaFormFieldStyles, extendedStyles, overridedStyles);
    const [value, setValue] = useState(defaultValue);
    const [error, setError] = useState("");
    const textLabel = typeof label === "string" ? label : undefined;

    const validate = (value: string) => {
        let errorMessage: string | undefined;

        if (required) {
            const invalidMessage = validateRequiredTextField(value, textLabel);

            if (invalidMessage) {
                errorMessage = requiredErrorMessage || invalidMessage;
            }
        }

        const trimmedValue = value.trim();

        if (!errorMessage && minLength && trimmedValue.length < minLength) {
            errorMessage =
                minLengthErrorMessage || siteMessage("MinimumCharacterLengthError", String(minLength)).toString();
        }

        errorMessage = onValidate?.({ isValid: !errorMessage, value: trimmedValue }) ?? errorMessage;

        setError(errorMessage || "");

        return !errorMessage;
    };

    useEffect(() => {
        const isValid = validate(value);
        onUpdate?.({ value, isValid });
    }, []);

    return (
        <TextArea
            {...styles.textArea}
            placeholder={textLabel ? translate("Enter {0}", textLabel) : undefined}
            {...textAreaProps}
            error={showError ? error : ""}
            label={label}
            minLength={minLength}
            onBlur={event => {
                onBlur?.(event);
                onUpdate?.({ value, isValid: !error });
            }}
            onChange={event => {
                onChange?.(event);
                const value = event.target.value;
                setValue(value);
                validate(value);
            }}
            required={required}
            rows={3}
            value={value}
        />
    );
};

export default TextAreaFormField;
